import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionQuery } from "../../../auth/hooks";
import { useClientBranding } from "../../../../shared/hooks";
import { useDepartments } from "../../../../shared/components/Filters/hooks";
import { clientBrandingApi } from "../../../../shared/api";
import type { UpdateClientBrandingRequest } from "../../../../shared/api";
import { qk } from "../../../../lib/queryKeys";
import {
  clientManagementApi,
  type ResetUserPasswordResponse,
  type ServiceOverrides,
} from "../api/clientManagement";
import {
  BulkUploadTab,
  BrandingTab,
  ClientTab,
  ClientScopeSelector,
  DashboardAccessTab,
  DefaultsTab,
  DepartmentsTab,
  JobRolesTab,
  SettingsTabs,
  UserManagementTab,
} from "../components";
import {
  buildPayload,
  makePreview,
  validateHex,
  validatePalette,
  toFormState,
} from "../utils/branding";
import {
  emptyFormState,
  emptyManagementState,
  emptyCreateClientForm,
  type BrandingFormState,
  type ClientManagementState,
  type CreateClientFormState,
} from "../types";

export const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const clientId = session?.user?.client_id ?? null;
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const [selectedClientId, setSelectedClientId] = useState("");
  const effectiveClientId = isCentralAdmin ? selectedClientId || null : clientId;

  const { data, isLoading, error } = useClientBranding(effectiveClientId);
  const departmentsQuery = useDepartments();
  const [formState, setFormState] =
    useState<BrandingFormState>(emptyFormState);
  const [managementState, setManagementState] =
    useState<ClientManagementState>(emptyManagementState);
  const [createClientForm, setCreateClientForm] =
    useState<CreateClientFormState>(emptyCreateClientForm);
  const [createClientResult, setCreateClientResult] = useState<
    Awaited<ReturnType<typeof clientManagementApi.createClient>> | null
  >(null);
  const [loadedClientId, setLoadedClientId] = useState<string | null>(null);
  const previousClientIdRef = useRef<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState("branding");
  const [updatingUserServicesId, setUpdatingUserServicesId] = useState<
    string | null
  >(null);
  const tabs = [
    { value: "client", label: isCentralAdmin ? "Clients" : "Client" },
    { value: "branding", label: "Branding" },
    { value: "job-roles", label: "Job Roles" },
    { value: "departments", label: "Departments" },
    ...(isCentralAdmin
      ? [
          { value: "defaults", label: "Defaults" },
          { value: "dashboard-access", label: "Dashboard Access" },
          ...(effectiveClientId
            ? [{ value: "bulk-upload", label: "Bulk Upload" }]
            : []),
        ]
      : []),
    { value: "user-management", label: "User Management" },
  ];

  useEffect(() => {
    if (previousClientIdRef.current === effectiveClientId) return;
    previousClientIdRef.current = effectiveClientId;
    setFormState(emptyFormState);
    setErrors({});
    setLoadedClientId(null);
  }, [effectiveClientId]);

  useEffect(() => {
    if (!effectiveClientId) return;
    if (loadedClientId === effectiveClientId) return;
    if (!data) return;

    setFormState(toFormState(data.data));
    setLoadedClientId(effectiveClientId);
  }, [data, effectiveClientId, loadedClientId]);

  useEffect(() => {
    if (!effectiveClientId) return;
    if (loadedClientId !== effectiveClientId) {
      queryClient.invalidateQueries({
        queryKey: qk.clientBranding(effectiveClientId),
      });
    }
  }, [effectiveClientId, loadedClientId, queryClient]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateClientBrandingRequest) =>
      clientBrandingApi.updateClientBranding(effectiveClientId as string, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(
        qk.clientBranding(effectiveClientId),
        response
      );
      setFormState(toFormState(response.data));
    },
  });

  const clientUsersQuery = useQuery({
    queryKey: qk.clientUsers(effectiveClientId),
    queryFn: () =>
      clientManagementApi.listClientUsers(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (tabs.some((tab) => tab.value === activeTab)) return;
    setActiveTab("client");
  }, [activeTab, tabs]);

  const jobRolesQuery = useQuery({
    queryKey: qk.clientJobRoles(effectiveClientId),
    queryFn: () =>
      clientManagementApi.listJobRoles(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 5 * 60 * 1000,
  });

  const createDepartmentMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDepartment(effectiveClientId as string, payload),
    onSuccess: () => {
      setManagementState((prev) => ({
        ...prev,
        departmentName: "",
        departmentActive: true,
      }));
      queryClient.invalidateQueries({
        queryKey: ["filter-options", "departments"],
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralDepartments(effectiveClientId),
      });
    },
  });

  const updateDepartmentMutation = useMutation({
    mutationFn: ({
      departmentId,
      payload,
    }: {
      departmentId: string;
      payload: { name?: string; is_active?: boolean };
    }) => clientManagementApi.updateDepartment(departmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["filter-options", "departments"],
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralDepartments(effectiveClientId),
      });
    },
  });

  const deleteDepartmentMutation = useMutation({
    mutationFn: (departmentId: string) =>
      clientManagementApi.deleteDepartment(departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["filter-options", "departments"],
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralDepartments(effectiveClientId),
      });
    },
  });

  const createJobRoleMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createJobRole(effectiveClientId as string, payload),
    onSuccess: () => {
      setManagementState((prev) => ({
        ...prev,
        jobRoleName: "",
        jobRoleActive: true,
      }));
      queryClient.invalidateQueries({
        queryKey: qk.clientJobRoles(effectiveClientId),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralJobRoles(effectiveClientId),
      });
    },
  });

  const updateJobRoleMutation = useMutation({
    mutationFn: ({
      jobRoleId,
      payload,
    }: {
      jobRoleId: string;
      payload: { name?: string; is_active?: boolean };
    }) => clientManagementApi.updateJobRole(jobRoleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientJobRoles(effectiveClientId),
      });
    },
  });

  const deleteJobRoleMutation = useMutation({
    mutationFn: (jobRoleId: string) => clientManagementApi.deleteJobRole(jobRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientJobRoles(effectiveClientId),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralJobRoles(effectiveClientId),
      });
    },
  });

  const defaultJobRolesQuery = useQuery({
    queryKey: qk.defaultJobRoles(),
    queryFn: () => clientManagementApi.listDefaultJobRoles(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const createDefaultJobRoleMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDefaultJobRole(payload),
    onSuccess: () => {
      setManagementState((prev) => ({
        ...prev,
        jobRoleName: "",
        jobRoleActive: true,
      }));
      queryClient.invalidateQueries({
        queryKey: qk.defaultJobRoles(),
      });
    },
  });

  const updateDefaultJobRoleMutation = useMutation({
    mutationFn: ({
      jobRoleId,
      payload,
    }: {
      jobRoleId: string;
      payload: { name?: string; is_active?: boolean };
    }) => clientManagementApi.updateDefaultJobRole(jobRoleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.defaultJobRoles(),
      });
    },
  });

  const deleteDefaultJobRoleMutation = useMutation({
    mutationFn: (jobRoleId: string) =>
      clientManagementApi.deleteDefaultJobRole(jobRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.defaultJobRoles(),
      });
    },
  });

  const defaultDepartmentsQuery = useQuery({
    queryKey: qk.defaultDepartments(),
    queryFn: () => clientManagementApi.listDefaultDepartments(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const dashboardSettingsQuery = useQuery({
    queryKey: qk.dashboardSettings(effectiveClientId),
    queryFn: () =>
      clientManagementApi.listClientDashboardSettings(effectiveClientId as string),
    enabled: isCentralAdmin && !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const updateDashboardSettingMutation = useMutation({
    mutationFn: ({
      dashboardKey,
      payload,
    }: {
      dashboardKey: string;
      payload: { is_enabled?: boolean; ai_enabled?: boolean | null };
    }) =>
      clientManagementApi.updateClientDashboardSetting(
        effectiveClientId as string,
        dashboardKey,
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.dashboardSettings(effectiveClientId),
      });
    },
  });

  const createDefaultDepartmentMutation = useMutation({
    mutationFn: (payload: { name: string; is_active: boolean }) =>
      clientManagementApi.createDefaultDepartment(payload),
    onSuccess: () => {
      setManagementState((prev) => ({
        ...prev,
        departmentName: "",
        departmentActive: true,
      }));
      queryClient.invalidateQueries({
        queryKey: qk.defaultDepartments(),
      });
    },
  });

  const updateDefaultDepartmentMutation = useMutation({
    mutationFn: ({
      departmentId,
      payload,
    }: {
      departmentId: string;
      payload: { name?: string; is_active?: boolean };
    }) => clientManagementApi.updateDefaultDepartment(departmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.defaultDepartments(),
      });
    },
  });

  const deleteDefaultDepartmentMutation = useMutation({
    mutationFn: (departmentId: string) =>
      clientManagementApi.deleteDefaultDepartment(departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.defaultDepartments(),
      });
    },
  });

  const assignUserToClientMutation = useMutation({
    mutationFn: (userId: string) =>
      clientManagementApi.assignUserToClient(
        effectiveClientId as string,
        userId
      ),
    onSuccess: () => {
      setManagementState((prev) => ({
        ...prev,
        assignUserId: "",
      }));
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const removeUserFromClientMutation = useMutation({
    mutationFn: (userId: string) =>
      clientManagementApi.removeUserFromClient(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const assignUserToDepartmentMutation = useMutation({
    mutationFn: ({
      departmentId,
      userId,
    }: {
      departmentId: string;
      userId: string;
    }) => clientManagementApi.assignUserToDepartment(departmentId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const removeUserFromDepartmentMutation = useMutation({
    mutationFn: ({
      departmentId,
      userId,
    }: {
      departmentId: string;
      userId: string;
    }) => clientManagementApi.removeUserFromDepartment(departmentId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const assignUserToJobRoleMutation = useMutation({
    mutationFn: ({
      jobRoleId,
      userId,
    }: {
      jobRoleId: string;
      userId: string;
    }) => clientManagementApi.assignUserToJobRole(jobRoleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const removeUserFromJobRoleMutation = useMutation({
    mutationFn: ({
      jobRoleId,
      userId,
    }: {
      jobRoleId: string;
      userId: string;
    }) => clientManagementApi.removeUserFromJobRole(jobRoleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
    },
  });

  const updateUserServicesMutation = useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: Partial<ServiceOverrides>;
    }) => clientManagementApi.updateUserServices(userId, payload),
    onMutate: ({ userId }) => {
      setUpdatingUserServicesId(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.clientUsers(effectiveClientId),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centralUsers("all"),
      });
    },
    onSettled: () => {
      setUpdatingUserServicesId(null);
    },
  });

  const resetUserPasswordMutation = useMutation<
    ResetUserPasswordResponse,
    unknown,
    {
      userId: string;
      temporary: boolean;
      sendEmail: boolean;
      password?: string;
      loginUrl?: string;
    }
  >({
    mutationFn: ({
      userId,
      temporary,
      sendEmail,
      password,
      loginUrl,
    }: {
      userId: string;
      temporary: boolean;
      sendEmail: boolean;
      password?: string;
      loginUrl?: string;
    }) =>
      clientManagementApi.resetUserPassword(userId, {
        temporary,
        send_email: sendEmail,
        ...(password ? { password } : {}),
        ...(loginUrl ? { login_url: loginUrl } : {}),
      }),
  });

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const createClientMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      is_active: boolean;
      learning_locker_enabled: boolean;
      occupational_health_enabled: boolean;
      wearables_enabled: boolean;
      akea_life_enabled: boolean;
    }) =>
      clientManagementApi.createClient(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: qk.clients() });
      setCreateClientForm(emptyCreateClientForm);
      setCreateClientResult(response);
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: (
      payload: Partial<{
        enable_anonymisation: boolean;
        learning_locker_enabled: boolean;
        occupational_health_enabled: boolean;
        wearables_enabled: boolean;
        akea_life_enabled: boolean;
      }>
    ) =>
      clientManagementApi.updateClient(effectiveClientId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clientDetails(effectiveClientId) });
      queryClient.invalidateQueries({ queryKey: qk.clients() });
    },
  });

  const centralUsersAllQuery = useQuery({
    queryKey: qk.centralUsers("all"),
    queryFn: () => clientManagementApi.listCentralUsers(),
    enabled: isCentralAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const clientDetailsQuery = useQuery({
    queryKey: qk.clientDetails(effectiveClientId),
    queryFn: () =>
      clientManagementApi.getClientDetails(effectiveClientId as string),
    enabled: !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const selectedDepartmentsQuery = useQuery({
    queryKey: qk.centralDepartments(effectiveClientId),
    queryFn: () =>
      clientManagementApi.listCentralDepartments(effectiveClientId),
    enabled: isCentralAdmin && !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const selectedClient = isCentralAdmin
    ? clientsQuery.data?.data?.find((client) => client.id === selectedClientId) ??
      null
    : null;
  const departmentsList = isCentralAdmin
    ? selectedDepartmentsQuery.data?.data ?? []
    : departmentsQuery.data?.data ?? [];
  const departmentsLoading = isCentralAdmin
    ? selectedDepartmentsQuery.isLoading
    : departmentsQuery.isLoading;

  const handleBrandingChange = (
    field: keyof BrandingFormState,
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleManagementChange = (
    field: keyof ClientManagementState,
    value: string | boolean
  ) => {
    setManagementState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateClientChange = (
    field: keyof CreateClientFormState,
    value: string | boolean
  ) => {
    setCreateClientForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setAssignUserId = (value: string) =>
    setManagementState((prev) => ({
      ...prev,
      assignUserId: value,
    }));

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const colorFields: (keyof BrandingFormState)[] = [
      "primary_color",
      "secondary_color",
      "accent_color",
      "background_color",
      "text_color",
      "dark_primary_color",
      "dark_secondary_color",
      "dark_accent_color",
      "dark_background_color",
      "dark_text_color",
      "chart_primary_color",
      "chart_secondary_color",
      "chart_accent_color",
      "dark_chart_primary_color",
      "dark_chart_secondary_color",
      "dark_chart_accent_color",
    ];

    colorFields.forEach((field) => {
      const value = formState[field];
      if (!validateHex(value)) {
        nextErrors[field] = "Enter a valid hex colour.";
      }
    });

    const chartPaletteValidation = validatePalette(formState.chart_palette);
    if (!chartPaletteValidation.valid) {
      nextErrors.chart_palette = chartPaletteValidation.message || "Invalid.";
    }
    const darkChartPaletteValidation = validatePalette(
      formState.dark_chart_palette
    );
    if (!darkChartPaletteValidation.valid) {
      nextErrors.dark_chart_palette =
        darkChartPaletteValidation.message || "Invalid.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!effectiveClientId) return;
    if (!validate()) return;
    mutation.mutate(buildPayload(formState));
  };

  const lightPreview = useMemo(
    () => makePreview(formState, "light"),
    [formState]
  );
  const darkPreview = useMemo(
    () => makePreview(formState, "dark"),
    [formState]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage client configuration, branding, and admin settings in one place.
        </Typography>
      </Box>

      <ClientScopeSelector
        isCentralAdmin={isCentralAdmin}
        selectedClientId={selectedClientId}
        selectedClient={selectedClient}
        clients={clientsQuery.data?.data ?? []}
        onChange={setSelectedClientId}
      />

      <SettingsTabs
        value={activeTab}
        tabs={tabs}
        onChange={setActiveTab}
      />

      {activeTab === "client" && (
        <ClientTab
          effectiveClientId={effectiveClientId}
          isLoading={clientDetailsQuery.isLoading}
          error={clientDetailsQuery.error}
          client={clientDetailsQuery.data?.data ?? null}
          isCentralAdmin={isCentralAdmin}
          onToggleAnonymisation={(value) =>
            updateClientMutation.mutate({ enable_anonymisation: value })
          }
          onToggleClientService={(field, value) =>
            updateClientMutation.mutate({ [field]: value })
          }
          anonymisationPending={updateClientMutation.isPending}
          createClientForm={createClientForm}
          onCreateClientChange={handleCreateClientChange}
          onCreateClient={() =>
            createClientMutation.mutate({
              name: createClientForm.name.trim(),
              is_active: createClientForm.is_active,
              learning_locker_enabled: createClientForm.learning_locker_enabled,
              occupational_health_enabled:
                createClientForm.occupational_health_enabled,
              wearables_enabled: createClientForm.wearables_enabled,
              akea_life_enabled: createClientForm.akea_life_enabled,
            })
          }
          createClientPending={createClientMutation.isPending}
          createClientResult={createClientResult}
          onDismissCreateClientResult={() => setCreateClientResult(null)}
        />
      )}

      {activeTab === "branding" && (
        <BrandingTab
          effectiveClientId={effectiveClientId}
          isLoading={isLoading}
          error={error}
          formState={formState}
          errors={errors}
          onChange={handleBrandingChange}
          onSubmit={handleSubmit}
          isSaving={mutation.isPending}
          isSaveError={mutation.isError}
          isSaveSuccess={mutation.isSuccess}
          lightPreview={lightPreview}
          darkPreview={darkPreview}
        />
      )}

      {activeTab === "job-roles" && (
        <JobRolesTab
          effectiveClientId={effectiveClientId}
          managementState={managementState}
          onChange={handleManagementChange}
          createJobRoleMutation={createJobRoleMutation}
          updateJobRoleMutation={updateJobRoleMutation}
          deleteJobRoleMutation={deleteJobRoleMutation}
          jobRolesQuery={jobRolesQuery}
        />
      )}

      {activeTab === "departments" && (
        <DepartmentsTab
          effectiveClientId={effectiveClientId}
          managementState={managementState}
          onChange={handleManagementChange}
          createDepartmentMutation={createDepartmentMutation}
          updateDepartmentMutation={updateDepartmentMutation}
          deleteDepartmentMutation={deleteDepartmentMutation}
          departmentsLoading={departmentsLoading}
          departmentsList={departmentsList}
        />
      )}

      {activeTab === "defaults" && isCentralAdmin && (
        <DefaultsTab
          managementState={managementState}
          onChange={handleManagementChange}
          defaultDepartmentsQuery={defaultDepartmentsQuery}
          createDefaultDepartmentMutation={createDefaultDepartmentMutation}
          updateDefaultDepartmentMutation={updateDefaultDepartmentMutation}
          deleteDefaultDepartmentMutation={deleteDefaultDepartmentMutation}
          defaultJobRolesQuery={defaultJobRolesQuery}
          createDefaultJobRoleMutation={createDefaultJobRoleMutation}
          updateDefaultJobRoleMutation={updateDefaultJobRoleMutation}
          deleteDefaultJobRoleMutation={deleteDefaultJobRoleMutation}
        />
      )}

      {activeTab === "dashboard-access" && isCentralAdmin && (
        <DashboardAccessTab
          effectiveClientId={effectiveClientId}
          dashboardSettingsQuery={dashboardSettingsQuery}
          updateDashboardSettingMutation={updateDashboardSettingMutation}
        />
      )}

      {activeTab === "bulk-upload" && isCentralAdmin && (
        <BulkUploadTab
          effectiveClientId={effectiveClientId}
          isCentralAdmin={isCentralAdmin}
        />
      )}

      {activeTab === "user-management" && (
        <UserManagementTab
          effectiveClientId={effectiveClientId}
          isCentralAdmin={isCentralAdmin}
          managementState={managementState}
          setAssignUserId={setAssignUserId}
          assignUserToClientMutation={assignUserToClientMutation}
          removeUserFromClientMutation={removeUserFromClientMutation}
          clientUsersQuery={clientUsersQuery}
          departmentsList={departmentsList}
          jobRolesQuery={jobRolesQuery}
          centralUsersAllQuery={centralUsersAllQuery}
          updateUserServicesMutation={updateUserServicesMutation}
          resetUserPasswordMutation={resetUserPasswordMutation}
          updatingUserServicesId={updatingUserServicesId}
          assignUserToDepartmentMutation={assignUserToDepartmentMutation}
          removeUserFromDepartmentMutation={removeUserFromDepartmentMutation}
          assignUserToJobRoleMutation={assignUserToJobRoleMutation}
          removeUserFromJobRoleMutation={removeUserFromJobRoleMutation}
        />
      )}

      {!effectiveClientId &&
        isCentralAdmin && (
          <Box mt={2}>
            <Alert severity="info">
              Select a client to manage client-scoped settings.
            </Alert>
          </Box>
        )}
    </Box>
  );
};
